import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTickerData } from "../../api/tickersApi";
import Button from '@mui/material/Button';
import { Layout, Card, Row, Col, Select, Modal, Tabs, Empty } from 'antd';
import { isUUID } from "../../utils/strings";
import BenchmarkMetrics from "./BenchmarkMetrics";
import GeneralMetrics from "./GeneralMetrics";
import { useEnsureTickersLoaded } from "../../hooks/useEnsureTickersLoaded";
import TickerSearch from "../../components/TickerSearch/TickerSearch";
import PortfolioSelector  from "../Portfolio/PortfolioSelectors/PortfolioSelector";
import PageHeader from "../../components/PageHeader";
import PerformanceMetrics from "./PerformanceMetrics";
import {iconMapping} from "../../configs/AppsConfig";
import {useTickerData} from "../../hooks/useTickerData";
import DrawdownMetrics from "./DrawdownMetrics";
import {
  setSymbols, addSymbol, removeSymbol, setColor, setSelectedBenchmark, setActiveTab,
  selectComparisonSymbols, selectSelectedBenchmark, selectComparisonColors, selectActiveTab
} from '../../store/slices/comparisonV2.slice';

const { Option } = Select;
const { TabPane } = Tabs;


const makeWindowPaths = (windows, suffixFields)=>{
  const keyPaths = [];
  windows.forEach(window=>{
    suffixFields.forEach(suffix=>{
      keyPaths.push(`stats.windows.${window}.${suffix}`);
    });
  });
  return [...keyPaths, 'name']

}

const createPerformancePaths = (windows)=>{
  return makeWindowPaths(windows, ['hv', 'roc']);
}

const createDrawdownPaths = (windows)=>{
  return makeWindowPaths(windows, ['max_dd', 'max_dd_duration', 'max_dd_start', 'max_dd_end',
  'avg_dd_duration', 'avg_dd', 'pain_index', 'ulcer_index'])
}

const BENCHMARKS = ['spy', 'vxus', 'efa', 'iwr', 'eem', 'bnd', 'iwm', 'agg', 'vtv', 'qqq'];
const GROUP_FIELDS = {
  benchmarks: ['stats.benchmarks', 'name'],
  performanceShort: createPerformancePaths(['1_mo', '3_mo', '6_mo']),
  performanceLong: createPerformancePaths(['1_yr', '3_yr', '5_yr']),
  drawdowns: createDrawdownPaths(['1_yr', '3_yr', '5_yr'])
};


const mergeTickerData = (tickerData, tickers, colors) => {
  let i = 0;
  return Object.entries(tickerData).map(([symbol, data]) => {
    const baseObject = {
      ...data,
      symbol,
      color: colors[symbol] || '#000000', // Default color if not found
    };

    if (isUUID(symbol)) {
      i++;
      return {
        ...baseObject,
        name: data?.name || `Portfolio ${i}`,
        isPortfolio: true,
      };
    } else {
      const tickerObject = tickers.find(t => t.symbol === symbol) || {};
      return {
        ...baseObject,
        ...tickerObject,
        isPortfolio: false,
      };
    }
  });
};

const EmptyComparison = ({toggleSearch})=>{
  return (
    <Card bordered={false} className={'py-6'}>
      <Empty description={''} children={<Button onClick={toggleSearch} color={'info'} variant={'contained'}>Add Tickers or Portfolios</Button>} />
    </Card>
  )

}

const Comparison = () => {
  const dispatch = useDispatch();
  const symbols = useSelector(selectComparisonSymbols) || [];
  const selectedBenchmark = useSelector(selectSelectedBenchmark);
  const colors = useSelector(selectComparisonColors);
  const activeTab = useSelector(selectActiveTab);

  const [collection] = useState('stats');
  const [loading, setLoading] = useState(false);
  const [showSelectors, setShowSelectors] = useState(false);

  const { tickers } = useEnsureTickersLoaded(symbols || ['spy']);
  const tickerData = useTickerData(symbols || [], GROUP_FIELDS[activeTab], 'stats');

  const handleAddSymbols = useCallback((newSymbols) => {
    newSymbols.forEach(symbol => {
      if (!symbols.includes(symbol)) {
        dispatch(addSymbol(symbol));
        dispatch(setColor({ id: symbol }));
      }
    })
  }, [dispatch, symbols]);

  useEffect(() => {
    if (symbols && symbols.length > 0 && !loading) {
      setLoading(true);
      Promise.all([
        dispatch(fetchTickerData({
          symbols: symbols, fields: GROUP_FIELDS[activeTab], collection
        })).then((resp)=>{
          setLoading(false);
        })
      ]).catch((error) => setLoading(false));
    }
  }, [dispatch, symbols, activeTab, selectedBenchmark, collection]);


  const handleRemoveSymbol = useCallback((symbolToRemove) => {
    dispatch(removeSymbol(symbolToRemove));
  }, [dispatch]);

  const handleBenchmarkChange = useCallback((value) => {
    dispatch(setSelectedBenchmark(value));
  }, [dispatch]);

  const handleTabChange = useCallback((newActiveTab) => {
    dispatch(setActiveTab(newActiveTab));
  }, [dispatch]);
  const mergedData = useMemo(() => {
    return mergeTickerData(tickerData, tickers, colors);
  }, [tickerData, tickers, colors]);

  const handleClearAllTickers = useCallback(() => {
    setSymbols([]);
  }, [dispatch]);


  const isReady = !loading && Object.keys(tickerData).length > 0 && tickerData[Object.keys(tickerData)[0]].hasOwnProperty('stats')


  const renderContent = () => {
    if (!mergedData.length) return <EmptyComparison toggleSearch={()=>setShowSelectors(true)} />;

    return (
      <Row gutter={[16, 16]}>
        <Col span={24}>
            <Tabs tabPosition={'left'}  activeKey={activeTab} onChange={handleTabChange}>

              <TabPane tab="Benchmarks" key="benchmarks">
                <Select
                  value={selectedBenchmark}
                  style={{ width: 120, marginBottom: 16 }}
                  onChange={handleBenchmarkChange}
                >
                  {BENCHMARKS.map(benchmark => (
                    <Option key={benchmark} value={benchmark}>{benchmark.toUpperCase()}</Option>
                  ))}
                </Select>
                {isReady && (
                  <BenchmarkMetrics tickerData={mergedData} selectedBenchmark={selectedBenchmark} />
                )}
              </TabPane>
              <TabPane tab="Performance (Short)" key="performanceShort">
                {isReady&& (
                    <PerformanceMetrics tickerData={mergedData} performanceType="short" colors={colors} />
                )}
              </TabPane>
              <TabPane tab="Performance (Long)" key="performanceLong">
                <PerformanceMetrics tickerData={mergedData} performanceType="long" colors={colors} />
              </TabPane>
              <TabPane tab="Drawdowns" key="drawdowns">
                {isReady && (
                  <DrawdownMetrics tickerData={mergedData} />
                )}
              </TabPane>
            </Tabs>
        </Col>
      </Row>
    );
  };

  return (
    <Layout>
      <PageHeader title={'Comparison'}
                  logoUrl={iconMapping.performanceComparison}
                  actions={[
                      <Button onClick={()=>setShowSelectors(true)} color={'info'} variant={showSelectors?'outlined':'contained'}>
                        Search Tickers & Portfolios
                      </Button>,
                      <Button onClick={handleClearAllTickers} color={'error'} variant={'outlined'}>
                        Clear All
                      </Button>
                  ]}
      />
      {renderContent()}
      <Modal open={showSelectors} width={1000} onOk={null} onCancel={()=>setShowSelectors(false)}>
          <Row style={{padding: '20px 5px'}} justify={'center'} align={'top'} wrap={false}>
            <Col flex={'0 0 500px'}>
              <TickerSearch
                  onTickersSubmit={(tickers)=>handleAddSymbols(tickers.map(t=>t.symbol))}
              />
            </Col>
            <Col flex={'1 1 400px'}>
              <PortfolioSelector
                  onSelect={(portfolios)=>handleAddSymbols(portfolios.map(p=>p.id))}
              />
            </Col>
          </Row>
      </Modal>
    </Layout>
  );
};

export default Comparison;